import { debounce, requestIdleCallback, viewTransition } from "@theme/utilities";
import { Component } from "@theme/component";

const OFFSET = 8;

export class FloatingPanelComponent extends Component {
  #anchor = null;

  #updatePosition = async () => {
    viewTransition.current && (await viewTransition.current);

    const anchor = this.#anchor;
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    this.style.position = "fixed";
    this.style.top = `${anchorRect.bottom + OFFSET}px`;
    this.style.left = `${anchorRect.left}px`;

    const rect = this.getBoundingClientRect();
    if (rect.right > viewportWidth) {
      this.style.left = `${Math.max(OFFSET, anchorRect.left - (rect.right - viewportWidth + OFFSET))}px`;
    }
    if (rect.left < 0) {
      this.style.left = `${OFFSET}px`;
    }
  };

  #resizeListener = debounce(() => {
    const parent = this.closest("details");
    const closeOnResize = this.dataset.closeOnResize === "true";
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute("open");
    }
    this.#updatePosition();
  }, 100);

  #scrollListener = () => this.#updatePosition();

  #toggleListener = () => {
    if (this.closest("details")?.open) this.#updatePosition();
  };

  connectedCallback() {
    super.connectedCallback();
    this.#anchor = this.closest("details")?.querySelector(":scope > summary") ?? this.parentElement;

    // Only float/fix panels that CSS already positions absolute (horizontal +
    // mobile-pills contexts). Vertical/drawer accordions render in normal
    // flow and must be left untouched.
    if (getComputedStyle(this).position !== "absolute") return;

    window.addEventListener("resize", this.#resizeListener);
    window.addEventListener("scroll", this.#scrollListener, true);
    this.closest("details")?.addEventListener("toggle", this.#toggleListener);

    requestIdleCallback(() => this.#updatePosition());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this.#resizeListener);
    window.removeEventListener("scroll", this.#scrollListener, true);
    this.closest("details")?.removeEventListener("toggle", this.#toggleListener);
  }
}

customElements.get("floating-panel-component") || customElements.define("floating-panel-component", FloatingPanelComponent);